import '../bootstrap/disableRechartsAnimations';
import '../styles/globals.css';
import '@zillow/zillow-map-control/dist/ZillowMapControl.css';

import type { AppProps } from 'next/app';
import { RpErrorBoundary, useBootstrap } from '@zg-rentals/rp-bootstrap-browser';
import { Head } from '@zg-rentals/next/app';
import { useRegisterNotification } from '@zg-rentals/register-notification';
import { useRouter } from 'next/router';
import { useComscorePageTracking } from '@zg-rentals/comscore';
import { trackComscoreEvent } from '../analytics';
import { setClient as setCustomerAnalyticsClient } from '@zg-rentals/ts-customer-analytics-client';
import { setClient as setRentalManagerClient } from '@zg-rentals/ts-rental-manager-api-client';
import { httpClient, rentalManagerHttpClient } from '../httpClient';
import { useSiftScience } from '@zg-rentals/sift-science';
import { Auth, useAuth } from '../AuthProvider';
import { ToastProvider } from '@zillow/constellation-10';
import { DefaultQueryProvider } from '@zg-rentals/react-query5';
import { ZRAMPageLayout } from '../components/layouts';
import { ThemeProvider } from 'styled-components';
import { CometTheme } from '@zg-rentals/zrs-comet';
import type { CustomerAnalyticsNextPage } from '../types';
import '@zillow/constellation-fonts/zillow-fonts.css';

setCustomerAnalyticsClient(httpClient);
setRentalManagerClient(rentalManagerHttpClient);

type AppPropsWithLayout = AppProps & {
  Component: CustomerAnalyticsNextPage;
};

const MyApp = ({ Component, pageProps }: AppPropsWithLayout) => {
  useBootstrap(() => import('../bootstrap/browser'));

  const router = useRouter();

  const { user } = useAuth();

  useComscorePageTracking(() => trackComscoreEvent({}), router.pathname);

  useSiftScience({ email: user?.email, isImpersonator: Boolean(user?.impersonatorUserToken) });

  useRegisterNotification({ ref: 'CUSTOMER_ANALYTICS_WEB_NEXT' });

  const getLayout = Component.getLayout ?? ((page) => <ZRAMPageLayout>{page}</ZRAMPageLayout>);

  const title = Component.title || 'Analytics Overview';

  const initialUser = typeof window !== 'undefined' ? window.__USER_INFO__ : undefined;

  return (
    <>
      <Head applicationName="customer-analytics-web-next" buildNumber={process.env.NEXT_PUBLIC_BUILD_NUMBER}>
        <title>{title}</title>
      </Head>

      <RpErrorBoundary>
        <DefaultQueryProvider
          defaultOptions={{
            queries: {
              staleTime: 20 * 1000,
            },
          }}
        >
          <ThemeProvider theme={CometTheme}>
            <ToastProvider>
              <Auth.Provider initialData={initialUser}>{getLayout(<Component {...pageProps} />)}</Auth.Provider>
            </ToastProvider>
          </ThemeProvider>
        </DefaultQueryProvider>
      </RpErrorBoundary>
    </>
  );
};

export default MyApp;
